import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tickets, type Envelope } from '@repletoryreach/sdk-api';
import { Button, Group, Skeleton, Stack } from '@repletoryreach/sdk-ui';
import { Alert, BreadcrumbTrail } from '@repletoryreach/sdk-ui/data-display';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { HelpButton } from '../../../app/guides/HelpButton';
import { useT } from '../../../i18n/I18nProvider';
import { useNow } from '../../inbox/components/SlaBadge';
import { useOrgTimezone } from '../../bookings/lib/data';
import { TICKETS_KEY, ticketErrorText, useLookups } from '../lib/data';
import { TicketHeader } from './Header';
import { Timeline } from './Timeline';
import { Composer } from './Composer';
import { ConversationsPanel, DetailsPanel, FieldsPanel, SlaPanel } from './Panels';
import { TasksPanel } from './Tasks';
import { LinkModal, MergeModal } from './Modals';
import '../tickets.css';

export default function TicketDetailPage() {
  const t = useT();
  const nav = useNavigate();
  const qc = useQueryClient();
  const { id = '' } = useParams();
  const tz = useOrgTimezone();
  const now = useNow(30_000);
  const lk = useLookups();
  const [merging, setMerging] = useState(false);
  const [linking, setLinking] = useState(false);

  const key = [TICKETS_KEY, 'ticket', id];
  const q = useQuery({ queryKey: key, queryFn: () => tickets.getTicket(id), enabled: !!id, refetchInterval: 30_000 });
  const ticket = q.data?.data;

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: [TICKETS_KEY] });
    for (const c of ticket?.conversations ?? []) void qc.invalidateQueries({ queryKey: ['conversation', c.id, 'messages'] });
  };

  const patch = useMutation({
    mutationFn: (p: tickets.UpdateTicketRequest) => tickets.updateTicket(id, p),
    onSuccess: (res) => {
      qc.setQueryData<Envelope<tickets.Ticket>>(key, res);
      void qc.invalidateQueries({ queryKey: [TICKETS_KEY, 'list'] });
      void qc.invalidateQueries({ queryKey: [TICKETS_KEY, 'board'] });
      notifySuccess(t('tickets.detail.saved'), res.data.number);
    },
    onError: (e) => notifyError(ticketErrorText(t, e), ticket?.number ?? t('tickets.title'))
  });

  if (q.isPending) {
    return (
      <Stack gap="lg">
        <Skeleton height={180} />
        <Skeleton height={320} />
      </Stack>
    );
  }
  if (q.error || !ticket) {
    return (
      <Stack gap="lg">
        <Alert color="error" role="alert">
          <Group gap="sm" justify="space-between" wrap="wrap">
            <span>{ticketErrorText(t, q.error)}</span>
            <Group gap="xs">
              <Button size="xs" variant="default" onClick={() => void q.refetch()}>{t('action.retry')}</Button>
              <Button size="xs" variant="subtle" onClick={() => nav('/tickets')}>{t('tickets.detail.backToQueue')}</Button>
            </Group>
          </Group>
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between" wrap="nowrap">
        <BreadcrumbTrail
          items={[
            { label: t('tickets.title'), href: '/tickets' },
            { label: ticket.number }
          ]}
          renderLink={(href, label) => <Link to={href}>{label}</Link>}
        />
        <HelpButton guide="tickets" />
      </Group>

      {ticket.merged_into_id && (
        <Alert color="info">
          <Group gap="sm" justify="space-between" wrap="wrap">
            <span>{t('tickets.detail.mergedInto')}</span>
            <Link to={`/tickets/${encodeURIComponent(ticket.merged_into_id)}`}>{t('tickets.detail.openMerged')}</Link>
          </Group>
        </Alert>
      )}

      <div data-guide="ticket-header">
      <TicketHeader ticket={ticket} lk={lk} tz={tz} busy={patch.isPending} onPatch={(p) => patch.mutate(p)} onMerge={() => setMerging(true)} />
      </div>

      <div className="rr-tickets__detail">
        <Stack gap="lg" className="rr-tickets__detail-main">
          <div data-guide="ticket-timeline"><Timeline ticket={ticket} /></div>
          <div data-guide="ticket-composer"><Composer ticket={ticket} onSent={refresh} onLink={() => setLinking(true)} /></div>
        </Stack>
        <Stack gap="lg" className="rr-tickets__detail-side">
          <div data-guide="ticket-sla"><SlaPanel ticket={ticket} now={now} /></div>
          <div data-guide="ticket-tasks"><TasksPanel ticket={ticket} lk={lk} tz={tz} onChanged={refresh} /></div>
          <FieldsPanel ticket={ticket} lk={lk} busy={patch.isPending} onSave={(fields) => patch.mutate({ fields })} />
          <DetailsPanel ticket={ticket} lk={lk} />
          <ConversationsPanel ticket={ticket} onLink={() => setLinking(true)} />
        </Stack>
      </div>

      <MergeModal
        ticket={ticket}
        opened={merging}
        onClose={() => setMerging(false)}
        onMerged={(target) => {
          notifySuccess(t('tickets.merge.done', { from: ticket.number, into: target.number }), t('tickets.title'));
          refresh();
          nav(`/tickets/${encodeURIComponent(target.id)}`);
        }}
      />
      <LinkModal
        ticket={ticket}
        opened={linking}
        onClose={() => setLinking(false)}
        onLinked={(res) => {
          qc.setQueryData<Envelope<tickets.Ticket>>(key, { data: res });
          notifySuccess(t('tickets.link.done'), ticket.number);
          refresh();
        }}
      />
    </Stack>
  );
}
